import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';

// Types pour définir la structure des données de compétences
export interface SkillData {
  name: string;
  value: number;
  description?: string;
  category?: string;
  subCategory?: string; // Pour catégoriser les compétences (ex: "data-science" ou "statistiques")
}

interface SkillRadarChartProps {
  skills: SkillData[];
  maxValue?: number;
  size?: number;
  animationDuration?: number;
  colors?: {
    area: string;
    point: string;
    grid: string;
    text: string;
  };
  title?: string;
  id: string; // Pour l'accessibilité et les animations
  showLegend?: boolean;
  filter?: (skill: SkillData) => boolean; // Fonction pour filtrer les compétences à afficher
  chartOrder?: number; // Ordre d'affichage
  visible?: boolean; // Visibilité du graphique
}

// Styles
const ChartContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  display:
  flex-direction: column;
`;

const ChartTitle = styled.h3`
  text-align: center;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.2rem;
`;

const SVGContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
    max-height: 100%;
    outline: none !important; /* Empêcher l'effet d'illumination au clic/focus */
    -webkit-tap-highlight-color: transparent; /* Désactiver la surbrillance sur mobile */
  }
  
  @media (max-width: 768px) {
    /* Sur mobile, assurer que le SVG utilise tout l'espace */
    flex: 1 0 auto;
    aspect-ratio: 1 / 1;
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.5rem;
`;

const LegendItem = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  &::before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${props => props.color};
    margin-right: 0.5rem;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  pointer-events: none;
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}ee`};
  color: ${({ theme }) => theme.colors.text};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-width: 250px;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid ${({ theme }) => `${theme.colors.primary}30`};
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--tooltip-color, ${({ theme }) => theme.colors.primary});
    font-size: 1rem;
  }
  
  p {
    margin: 0 0 0.3rem 0;
    font-size: 0.85rem;
  }
  
  .value {
    display: inline-block;
    padding: 0.15rem 0.4rem;
    margin-top: 0.3rem;
    background: var(--tooltip-color-bg, ${({ theme }) => `${theme.colors.primary}20`});
    border-radius: 4px;
    font-weight: 600;
    color: var(--tooltip-color, ${({ theme }) => theme.colors.primary});
  }
`;

// Composant principal du graphique radar
const SkillRadarChart: React.FC<SkillRadarChartProps> = ({
  skills,
  maxValue = 100,
  size = 300,
  animationDuration = 1000,
  colors = {
    area: 'rgba(52, 152, 219, 0.6)',
    point: '#3498db',
    grid: 'rgba(255, 255, 255, 0.2)',
    text: '#ffffff'
  },
  title,
  id,
  showLegend = false,
  filter,
  chartOrder,
  visible
}) => {
  // @ts-ignore
  const { t } = useTranslation('skills');
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Niveau de compétence selon la valeur
  const getSkillLevel = (value: number): string => {
    if (value < 20) return t('common:skillLevels.beginner');
    if (value < 40) return t('common:skillLevels.intermediate');
    if (value < 60) return t('common:skillLevels.advanced');
    if (value < 80) return t('common:skillLevels.confirmed');
    return t('common:skillLevels.expert');
  };
  
  // Observateur de redimensionnement
  const resizeObserver = useRef<ResizeObserver | null>(null);
  
  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile(); // Vérification initiale
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Gestionnaire pour masquer le tooltip quand on quitte le graphique (uniquement pour PC)
  useEffect(() => {
    if (!isMobile) {
      const hideTooltipOnExit = () => {
        if (tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      };

      const containerElement = containerRef.current;
      if (containerElement) {
        containerElement.addEventListener('mouseleave', hideTooltipOnExit);
      }

      return () => {
        if (containerElement) {
          containerElement.removeEventListener('mouseleave', hideTooltipOnExit);
        }
      };
    }
  }, [isMobile]);

  // Gestionnaire pour masquer le tooltip lors du clic en dehors (pour mobile)
  useEffect(() => {
    if (isMobile) {
      const handleDocumentClick = (event: MouseEvent) => {
        if (tooltipRef.current && containerRef.current) {
          // Vérifier si le clic est en dehors du conteneur du graphique
          if (!containerRef.current.contains(event.target as Node)) {
            d3.select(tooltipRef.current).style('opacity', 0);
          }
        }
      };

      document.addEventListener('click', handleDocumentClick);

      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }
  }, [isMobile]);
  
  // Fonction principale pour dessiner le graphique radar
  const drawChart = () => {
    if (!svgRef.current || !containerRef.current || skills.length < 3) return;
    
    // Nettoyer le SVG existant
    d3.select(svgRef.current).selectAll('*').remove();
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const chartSize = Math.min(containerWidth, containerHeight);
    
    // Configuration du SVG pour l'accessibilité
    const svg = d3.select(svgRef.current)
      .attr('width', chartSize)
      .attr('height', chartSize)
      .attr('viewBox', `0 0 ${chartSize} ${chartSize}`)
      .attr('aria-labelledby', `${id}-title`)
      .attr('role', 'img')
      .style('pointer-events', 'auto')
      .style('cursor', 'default')
      .append('g')
      .attr('transform', `translate(${chartSize / 2}, ${chartSize / 2})`);
    
    // Sur PC, masquer le tooltip lors du mouvement sur le SVG en dehors des points/labels
    if (!isMobile) {
      d3.select(svgRef.current).on('mousemove', function(event) {
        // Si la souris n'est pas sur un point ou un label, masquer le tooltip
        // Vérifier si l'élément sous le curseur est un point ou un label
        const elements = document.elementsFromPoint(event.clientX, event.clientY);
        
        // Vérifier si un des éléments est un point ou un label
        const isOnInteractiveElement = elements.some(el => {
          if (el.tagName === 'circle') return true;
          
          // Pour les textes SVG, vérifier si le parent est le groupe de labels
          if (el.tagName === 'text') {
            // Pour les éléments SVG, nous devons vérifier la classe différemment
            const parent = el.parentElement;
            if (parent && parent.classList && parent.classList.contains('labels-group')) {
              return true;
            }
          }
          return false;
        });
        
        // Si on n'est pas sur un élément interactif, masquer le tooltip
        if (!isOnInteractiveElement && tooltipRef.current) {
          d3.select(tooltipRef.current).style('opacity', 0);
        }
      });
    }

    // Au lieu d'utiliser l'élément 'title' qui provoque l'apparition du tooltip,
    // on utilise un attribut aria-label pour l'accessibilité
    d3.select(svgRef.current)
      .attr('aria-label', title || 'Graphique radar des compétences');
    
    // Description pour l'accessibilité
    svg.append('desc')
      .text(`Graphique radar montrant les niveaux de compétences suivants: ${skills.map(s => `${s.name} (${s.value}%)`).join(', ')}`);
    
    // Calculer le rayon
    // Réduit légèrement pour laisser plus d'espace aux labels
    // Sur mobile, on réduit davantage pour que les labels ne débordent pas
    const radiusFactor = isMobile ? 0.75 : 0.8; // Augmenté pour occuper plus d'espace
    const radius = (chartSize / 2) * radiusFactor;
    
    // Échelle pour le rayon (valeurs)
    const radiusScale = d3.scaleLinear()
      .domain([0, maxValue])
      .range([0, radius]);
    
    // Configuration des axes (nombre d'axes = nombre de compétences)
    const angleSlice = (Math.PI * 2) / skills.length;
    
    // Créer les cercles concentriques (niveaux)
    const levels = 5;
    const levelGroups = svg.append('g')
      .attr('class', 'levels-group')
      .attr('aria-hidden', 'true');
    
    for (let i = 1; i <= levels; i++) {
      const levelValue = (maxValue / levels) * i;
      const levelRadius = radiusScale(levelValue);
      
      // Cercle de niveau
      levelGroups.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', levelRadius)
        .attr('fill', 'none')
        .attr('stroke', colors.grid)
        .attr('stroke-width', i === levels ? 1.5 : 0.8)
        .attr('stroke-opacity', i === levels ? 0.4 : 0.2);
    }
    
    // Créer les axes
    const axisGroups = svg.append('g')
      .attr('class', 'axis-group')
      .attr('aria-hidden', 'true');
    
    skills.forEach((_, i) => {
      const angle = i * angleSlice;
      const lineX = radius * Math.cos(angle - Math.PI / 2);
      const lineY = radius * Math.sin(angle - Math.PI / 2);
      
      axisGroups.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', lineX)
        .attr('y2', lineY)
        .attr('stroke', colors.grid)
        .attr('stroke-width', 0.8);
    });
    
    // Créer la forme du radar
    const radarGroup = svg.append('g')
      .attr('class', 'radar-group');
    
    // Fonction pour générer le chemin du polygone
    const radarLine = d3.lineRadial<SkillData>()
      .angle((_, i) => i * angleSlice)
      .radius(d => radiusScale(loaded ? d.value : 0))
      .curve(d3.curveLinearClosed);
    
    // Créer le dégradé pour le remplissage
    const gradientId = `radar-gradient-${id}`;
    const gradient = svg.append('defs')
      .append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%');
    
    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', colors.point)
      .attr('stop-opacity', 0.8);
    
    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', colors.area)
      .attr('stop-opacity', 0.6);
    
    // Dessiner le polygone
    const radarArea = radarGroup.append('path')
      .attr('d', radarLine(skills) as string)
      .attr('fill', `url(#${gradientId})`)
      .attr('stroke', colors.point)
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.8)
      .attr('fill-opacity', 0.6);
    
    // Animation d'entrée
    if (!loaded) {
      radarArea
        .transition()
        .duration(animationDuration)
        .attrTween('d', () => {
          return (t: number) => {
            const interpolatedData = skills.map(skill => {
              return {
                ...skill,
                value: skill.value * t
              };
            });
            return radarLine(interpolatedData) as string;
          };
        })
        .on('end', () => {
          // Afficher les labels avec une transition fluide à la fin de l'animation du radar
          labelsGroup.transition()
            .duration(300)
            .style('opacity', 1);
          
          setLoaded(true);
        });
    }
    
    // Ajouter les points pour chaque compétence
    const pointsGroup = svg.append('g')
      .attr('class', 'points-group');
    
    skills.forEach((skill, i) => {
      const angle = i * angleSlice;
      const x = radiusScale(skill.value) * Math.cos(angle - Math.PI / 2);
      const y = radiusScale(skill.value) * Math.sin(angle - Math.PI / 2);
      
      // Dessiner le point (plus petit sur mobile)
      const pointRadius = isMobile ? 6 : 5; // Augmentation de la taille pour plus grande zone de clic sur mobile
      const point = pointsGroup.append('circle')
        .attr('cx', loaded ? x : 0)
        .attr('cy', loaded ? y : 0)
        .attr('r', pointRadius)
        .attr('fill', colors.point)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', isMobile ? 1.5 : 1.5)
        .attr('data-name', skill.name)
        .attr('data-value', skill.value)
        .attr('role', 'graphics-symbol')
        .attr('aria-roledescription', 'point')
        .attr('aria-label', `${skill.name}: ${skill.value}%`);
      
      // Animation des points
      if (!loaded) {
        point.transition()
          .duration(animationDuration)
          .attr('cx', x)
          .attr('cy', y);
      }
      
      // Gestion des interactions
      point.on('mouseover', function(event) {
          // Ne pas afficher de tooltip sur mobile
          if (isMobile || !tooltipRef.current) return;
          
          const tooltip = d3.select(tooltipRef.current);
          
          // Définir les variables CSS pour la couleur du tooltip basée sur la couleur du groupe
          tooltip.style('--tooltip-color', colors.point);
          tooltip.style('--tooltip-color-bg', `${colors.point}20`);
          
          tooltip.style('opacity', 1);
          tooltip.html(`
            <h4>${skill.name}</h4>
            ${skill.category ? `<p>${skill.category}</p>` : ''}
            ${skill.description ? `<p>${skill.description}</p>` : ''}
            <span class="value">${getSkillLevel(skill.value)}</span>
          `);
          
          // Positionner le tooltip
          const svgRect = svgRef.current?.getBoundingClientRect();
          if (!svgRect) return;
          
          const tooltipElement = tooltipRef.current;
          const tooltipRect = tooltipElement.getBoundingClientRect();
          
          // Calcul de la position optimale
          let tooltipX = event.clientX - svgRect.left;
          let tooltipY = event.clientY - svgRect.top - tooltipRect.height - 10;
          
          // Éviter les débordements
          if (tooltipY < 10) {
            tooltipY = event.clientY - svgRect.top + 10;
          }
          
          if (tooltipX + tooltipRect.width > svgRect.width) {
            tooltipX = svgRect.width - tooltipRect.width - 10;
          }
          
          if (tooltipX < 10) {
            tooltipX = 10;
          }
          
          tooltip
            .style('left', `${tooltipX}px`)
            .style('top', `${tooltipY}px`);
        })
        .on('mouseout', function() {
          if (!tooltipRef.current) return;
          d3.select(tooltipRef.current).style('opacity', 0);
        });
      
      // Supprimer tous les gestionnaires d'événements sur mobile
      if (isMobile) {
        point.on('click', null)
          .on('touchstart', null)
          .on('touchend', null)
          .on('touchcancel', null);
      }
    });
    
    // Ajouter les labels avec simulation d'attraction/répulsion pour éviter les superpositions
    const labelsGroup = svg.append('g')
      .attr('class', 'labels-group')
      .style('opacity', loaded ? 1 : 0); // Masquer les labels initialement si le graphique n'est pas chargé

    // Structure pour stocker les positions initiales et finales des labels
    interface LabelPosition {
      x: number;
      y: number;
      width: number;
      height: number;
      anchor: string;
      angle: number;
      point: { x: number; y: number };
      skill: SkillData;
      index: number;
    }

    // Calculer les positions initiales des labels
    const labelPositions: LabelPosition[] = skills.map((skill, i) => {
      const angle = i * angleSlice;
      const pointX = radiusScale(skill.value) * Math.cos(angle - Math.PI / 2);
      const pointY = radiusScale(skill.value) * Math.sin(angle - Math.PI / 2);

      // Calculer la position initiale du label
      // Augmenter l'offset pour éloigner davantage les labels du centre
      const labelOffset = isMobile ? 20 : 35; // Augmenté pour éviter les boutons de commande
      
      // Ajuster le décalage vertical pour les axes spéciaux
      const isTopAxis = Math.abs(angle - 1.5 * Math.PI) < 0.3; // Élargi pour couvrir plus de la partie supérieure
      const isBottomAxis = Math.abs(angle - 0.5 * Math.PI) < 0.3; // Élargi pour couvrir plus de la partie inférieure
      
      // Augmenter l'ajustement vertical pour les axes du haut et du bas
      const verticalAdjustment = isTopAxis ? (isMobile ? 15 : 20) : 
                              isBottomAxis ? (isMobile ? -15 : -20) : 0;
      
      // Position initiale du label avec plus d'écartement
      const x = pointX + labelOffset * Math.cos(angle - Math.PI / 2);
      const y = pointY + labelOffset * Math.sin(angle - Math.PI / 2) - verticalAdjustment;
      
      // Déterminer l'alignement du texte
      const textAnchor = x > 5 ? 'start' : x < -5 ? 'end' : 'middle';

      // Estimation de la largeur du label
      const estTextWidth = isMobile ? 
        skill.name.length * 4 : // ~4px par caractère sur mobile
        skill.name.length * 5;  // ~5px par caractère sur desktop
      
      const width = estTextWidth;
      const height = isMobile ? 18 : 24; // Hauteur augmentée pour tenir compte du niveau affiché
      
      return {
        x,
        y,
        width,
        height,
        anchor: textAnchor,
        angle,
        point: { x: pointX, y: pointY },
        skill,
        index: i
      };
    });

    // Animation et simulation d'attraction/répulsion en temps réel
    // Créons d'abord des éléments SVG pour les labels
    const labelElements: any[] = [];
    const connectionLines: any[] = [];
    
    labelPositions.forEach(labelPos => {
      // Créer le label avec position initiale, mais sans texte
      const label = labelsGroup.append('text')
        .attr('x', labelPos.x)
        .attr('y', labelPos.y)
        .attr('text-anchor', labelPos.anchor)
        .attr('dominant-baseline', 'central')
        .attr('fill', colors.text)
        .attr('font-size', isMobile ? '10px' : '14px')
        .attr('font-weight', 'bold')
        .attr('text-shadow', '1px 1px 4px rgba(0, 0, 0, 0.8)')
        .style('cursor', isMobile ? 'pointer' : 'default'); // Curseur normal sur PC
        
      // Ajouter une zone de clic invisible plus grande pour mobile
      if (isMobile) {
        const touchArea = labelsGroup.append('rect')
          .attr('x', labelPos.x - 30) // Grande zone autour du label
          .attr('y', labelPos.y - 30)
          .attr('width', 60)
          .attr('height', 60)
          .attr('fill', 'transparent')
          .attr('stroke', 'none')
          .attr('pointer-events', 'all')
          .style('cursor', 'pointer');

        // Variables pour gérer le toucher long
        let touchTimeout: any = null;
        const touchDelay = 500; // 500ms pour considérer comme un appui long
        
        touchArea.on('touchstart', function(event) {
          if (!tooltipRef.current) return;
          event.preventDefault(); // Empêcher le scroll
          
          // Démarrer un timer pour détecter l'appui long
          touchTimeout = setTimeout(() => {
            const tooltip = d3.select(tooltipRef.current);
            
            // Afficher le tooltip
            tooltip.style('opacity', 1);
            tooltip.style('--tooltip-color', colors.point);
            tooltip.style('--tooltip-color-bg', `${colors.point}20`);
            
            tooltip.html(`
              <h4>${labelPos.skill.name}</h4>
              ${labelPos.skill.category ? `<p>${labelPos.skill.category}</p>` : ''}
              ${labelPos.skill.description ? `<p>${labelPos.skill.description}</p>` : ''}
              <span class="value">${labelPos.skill.value}% - ${getSkillLevel(labelPos.skill.value)}</span>
            `);
            
            // Positionner au centre pour mobile
            const svgRect = svgRef.current?.getBoundingClientRect();
            if (!svgRect) return;
            
            const tooltipElement = tooltipRef.current;
            if (!tooltipElement) return; // Vérification avant d'accéder à getBoundingClientRect
            const tooltipRect = tooltipElement.getBoundingClientRect();
            
            const centerX = svgRect.width / 2 - tooltipRect.width / 2;
            const centerY = svgRect.height / 2 - tooltipRect.height;
            
            tooltip
              .style('left', `${Math.max(10, centerX)}px`)
              .style('top', `${Math.max(10, centerY)}px`);
          }, touchDelay);
        });
        
        touchArea.on('touchend touchcancel', function() {
          // Annuler le timer si le toucher est relâché avant la fin du délai
          if (touchTimeout) {
            clearTimeout(touchTimeout);
            touchTimeout = null;
          }
        });
        
        // Supprimer le gestionnaire de clic standard pour éviter la double activation
        touchArea.on('click', null);
      }

      // Animation des labels
      if (!loaded) {
        label.transition()
          .duration(animationDuration)
          .attr('x', labelPos.x)
          .attr('y', labelPos.y);
      }
      
      // Gestion des interactions
      label.on('mouseover', function(event) {
          if (!tooltipRef.current) return;
          
          const tooltip = d3.select(tooltipRef.current);
          // Définir les variables CSS pour la couleur du tooltip basée sur la couleur du groupe
          tooltip.style('--tooltip-color', colors.point);
          tooltip.style('--tooltip-color-bg', `${colors.point}20`);
          
          tooltip.style('opacity', 1);
          tooltip.html(`
            <h4>${labelPos.skill.name}</h4>
            ${labelPos.skill.category ? `<p>${labelPos.skill.category}</p>` : ''}
            ${labelPos.skill.description ? `<p>${labelPos.skill.description}</p>` : ''}
            <span class="value">${getSkillLevel(labelPos.skill.value)}</span>
          `);
          
          // Positionner le tooltip
          const svgRect = svgRef.current?.getBoundingClientRect();
          if (!svgRect) return;
          
          const tooltipElement = tooltipRef.current;
          const tooltipRect = tooltipElement.getBoundingClientRect();
          
          // Calcul de la position optimale pour éviter les débordements
          let tooltipX = event.clientX - svgRect.left;
          let tooltipY = event.clientY - svgRect.top - tooltipRect.height - 10;
          
          // Garantir que le tooltip reste dans les limites du SVG
          if (tooltipY < 10) {
            tooltipY = event.clientY - svgRect.top + 20; // Position en dessous si trop haut
          }
          
          if (tooltipX + tooltipRect.width > svgRect.width - 10) {
            tooltipX = svgRect.width - tooltipRect.width - 10;
          }
          
          if (tooltipX < 10) {
            tooltipX = 10;
          }
          
          tooltip
            .style('left', `${tooltipX}px`)
            .style('top', `${tooltipY}px`);
        })
        .on('mouseout', function() {
          if (!tooltipRef.current) return;
          d3.select(tooltipRef.current).style('opacity', 0);
        })
        .on('click', function(event) {
          // Sur mobile uniquement, gérer le clic pour afficher/masquer le tooltip
          if (!isMobile || !tooltipRef.current) return;
          
          event.stopPropagation(); // Empêcher la propagation pour éviter de déclencher d'autres événements
          
          const tooltip = d3.select(tooltipRef.current);
          
          // Afficher le tooltip avec une opacité plus élevée sur mobile pour bien le voir
          tooltip.style('opacity', 1);

          // Définir les variables CSS pour la couleur du tooltip
          tooltip.style('--tooltip-color', colors.point);
          tooltip.style('--tooltip-color-bg', `${colors.point}20`);

          tooltip.html(`
            <h4>${labelPos.skill.name}</h4>
            ${labelPos.skill.category ? `<p>${labelPos.skill.category}</p>` : ''}
            ${labelPos.skill.description ? `<p>${labelPos.skill.description}</p>` : ''}
            <span class="value">${getSkillLevel(labelPos.skill.value)}</span>
          `);
          
          // Positionner le tooltip au centre pour mobile
          const svgRect = svgRef.current?.getBoundingClientRect();
          if (!svgRect) return;
          
          const tooltipElement = tooltipRef.current;
          if (!tooltipElement) return; // Vérification avant d'accéder à getBoundingClientRect
          const tooltipRect = tooltipElement.getBoundingClientRect();
          
          const centerX = svgRect.width / 2 - tooltipRect.width / 2;
          const centerY = svgRect.height / 2 - tooltipRect.height;
          
          tooltip
            .style('left', `${Math.max(10, centerX)}px`)
            .style('top', `${Math.max(10, centerY)}px`);
        });

      labelElements.push(label);
      
      // Ligne de connexion (invisible mais gardée pour référence)
      const connLine = radarGroup.append('line')
        .attr('x1', labelPos.point.x)
        .attr('y1', labelPos.point.y)
        .attr('x2', labelPos.x)
        .attr('y2', labelPos.y)
        .attr('stroke', colors.text)
        .attr('stroke-width', 0)  // Largeur à 0 pour les rendre invisibles
        .attr('stroke-opacity', 0)
        .attr('stroke-dasharray', '2,2');
      
      connectionLines.push(connLine);
    });
    
    // Paramètres de la simulation
    const minDistance = isMobile ? 18 : 25; // Distance minimale entre labels
    const minPointDistance = isMobile ? 10 : 15; // Distance minimale entre le label et son point
    
    // Ajouter une perturbation périodique pour maintenir le système en mouvement
    let frameCount = 0;
    
    // Fonction pour détecter les collisions
    const detectCollision = (a: LabelPosition, b: LabelPosition) => {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Calculer taille basée sur le texte réel
      const aWidth = a.skill.name.length * (isMobile ? 4 : 5);
      const bWidth = b.skill.name.length * (isMobile ? 4 : 5);
      const aHeight = isMobile ? 16 : 20;
      const bHeight = isMobile ? 16 : 20;
      
      // Distance minimale pour éviter la collision
      const minDist = Math.max(minDistance, (aWidth + bWidth) / 3 + (aHeight + bHeight) / 3);
      
      return distance < minDist;
    };
    
    // Animer la simulation en temps réel sans limite d'itérations
    let animationRunning = true; // Pour pouvoir arrêter l'animation si nécessaire
    
    // Fonction d'animation pour une itération
    const animateIteration = () => {
      if (!animationRunning) return;
      
      frameCount++;
      
      // Pour chaque label, appliquer les forces
      for (let i = 0; i < labelPositions.length; i++) {
        const labelA = labelPositions[i];
        
        // Détection des labels selon leur position
        const isTopLabel = Math.sin(labelA.angle - Math.PI/2) < -0.3; // Labels dans la moitié supérieure
        const isBottomLabel = Math.sin(labelA.angle - Math.PI/2) > 0.3; // Labels dans la moitié inférieure
        const isLeftLabel = Math.cos(labelA.angle - Math.PI/2) < -0.3; // Labels à gauche
        const isRightLabel = Math.cos(labelA.angle - Math.PI/2) > 0.3; // Labels à droite
        
        // Estimer la hauteur totale du label (basée sur le nombre de lignes potentielles)
        // Compte le nombre probable de lignes en fonction de la longueur du texte
        const textLength = labelA.skill.name.length;
        const labelMaxLineLength = isMobile ? 10 : 15;
        const estimatedLines = Math.max(1, Math.ceil(textLength / labelMaxLineLength));
        const labelLineHeight = isMobile ? 12 : 16;
        const totalHeight = estimatedLines * labelLineHeight;
        
        // Définir l'offset en fonction de la position et de la hauteur du label
        let offsetX = 0, offsetY = 0;
        
        // Augmenter significativement les offsets pour éloigner les labels des points
        if (isTopLabel) {
          // Pour les labels du haut, l'offset tient compte de la hauteur totale
          offsetY = -totalHeight - (isMobile ? 25 : 35); // Augmenté
        } else if (isBottomLabel) {
          offsetY = (isMobile ? 25 : 35); // Augmenté
        } else if (isLeftLabel) {
          offsetX = -(isMobile ? 35 : 45); // Augmenté
        } else if (isRightLabel) {
          offsetX = (isMobile ? 35 : 45); // Augmenté
        } else {
          // Pour les positions intermédiaires, calculer des offsets proportionnels
          const angle = labelA.angle - Math.PI/2;
          offsetX = Math.cos(angle) * (isMobile ? 30 : 40); // Augmenté
          offsetY = Math.sin(angle) * (isMobile ? 30 : 40); // Augmenté
          
          // Ajustement supplémentaire pour les labels en diagonale haute
          if (Math.sin(angle) < 0) {
            offsetY -= totalHeight / 1.5; // Augmenté
          }
        }
        
        // Position cible avec offset adapté à la position et à la hauteur du label
        const targetX = labelA.point.x + offsetX;
        const targetY = labelA.point.y + offsetY;
        
        // Force d'attraction vers la position cible
        const attractionStrength = 0.2;
        labelA.x += (targetX - labelA.x) * attractionStrength;
        labelA.y += (targetY - labelA.y) * attractionStrength;
        
        // Forces de répulsion entre labels
        const repulsionStrength = 0.5; // Augmenté
        for (let j = 0; j < labelPositions.length; j++) {
          if (i === j) continue;
          
          const labelB = labelPositions[j];
          
          // Vérifier s'il y a collision avec un autre label
          if (detectCollision(labelA, labelB)) {
            // Calculer la direction de répulsion
            const dx = labelA.x - labelB.x;
            const dy = labelA.y - labelB.y;
            const distance = Math.sqrt(dx * dx + dy * dy) || 0.1; // Éviter division par zéro
            
            // Normaliser et appliquer la force de répulsion
            const forceX = (dx / distance) * repulsionStrength;
            const forceY = (dy / distance) * repulsionStrength;
            
            labelA.x += forceX;
            labelA.y += forceY;
          }
        }
        
        // Limiter les déplacements extrêmes
        const maxDistance = radius * 0.4; // Augmenté
        const dx = labelA.x - labelA.point.x;
        const dy = labelA.y - labelA.point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > maxDistance) {
          const ratio = maxDistance / distance;
          labelA.x = labelA.point.x + dx * ratio;
          labelA.y = labelA.point.y + dy * ratio;
        }
        
        // Mettre à jour la position visuelle du label
        const label = labelElements[i];
        
        // Supprimer le contenu existant
        label.selectAll('*').remove();
        
        // Ajouter le nouveau contenu avec gestion multi-lignes
        const words = labelA.skill.name.split(' ');
        let line = '';
        let lineNumber = 0;
        const tspanLineHeight = 1.2;
        
        // Réutiliser la variable définie plus haut pour la longueur maximale de ligne
        const maxLineLength = isMobile ? 10 : 15;
        
        // Recréer le texte avec des retours à la ligne
        words.forEach(word => {
          // Sur mobile, abréger certains mots longs
          let processedWord = word;
          if (isMobile && word.length > 7) {
            if (word.toLowerCase() === 'temporelles') processedWord = 'temp.';
            else if (word.toLowerCase() === 'statistique') processedWord = 'stat.';
            else if (word.toLowerCase() === 'classification') processedWord = 'classif.';
            else if (word.toLowerCase() === 'dimension') processedWord = 'dim.';
            else if (word.length > 7) processedWord = word.substring(0, 5) + '.';
          }
          
          const testLine = line + processedWord + ' ';
          
          if (testLine.length > maxLineLength && line !== '') {
            label.append('tspan')
              .attr('x', labelA.x)
              .attr('y', labelA.y)
              .attr('dy', lineNumber * tspanLineHeight + 'em')
              .attr('text-anchor', labelA.anchor)
              .text(line.trim());
            
            line = processedWord + ' ';
            lineNumber++;
          } else {
            line = testLine;
          }
        });
        
        // Ajouter la dernière ligne
        if (line.trim() !== '') {
          label.append('tspan')
            .attr('x', labelA.x)
            .attr('y', labelA.y)
            .attr('dy', lineNumber * tspanLineHeight + 'em')
            .attr('text-anchor', labelA.anchor)
            .text(line.trim());
        }
        
        // Ajouter le niveau de compétence textuel sur une nouvelle ligne avec la couleur du groupe
        label.append('tspan')
          .attr('x', labelA.x)
          .attr('y', labelA.y)
          .attr('dy', (lineNumber + 1) * tspanLineHeight + 'em')
          .attr('text-anchor', labelA.anchor)
          .attr('fill', colors.point) // Utiliser la couleur du groupe
          .attr('font-weight', 'bold')
          .attr('font-size', isMobile ? '10px' : '14px') // Taille légèrement réduite pour le niveau
          .text(getSkillLevel(labelA.skill.value));
        
        // Mettre à jour la ligne de connexion
        connectionLines[i]
          .attr('x2', labelA.x)
          .attr('y2', labelA.y);
      }
      
      // Continuer l'animation indéfiniment
      requestAnimationFrame(animateIteration);
    };
    
    // Lancer l'animation
    setTimeout(() => {
      requestAnimationFrame(animateIteration);
    }, 100);
    
    // Arrêter l'animation quand le composant est démonté
    svgRef.current.addEventListener('remove', () => {
      animationRunning = false;
    });
  };
  
  // Créer le graphique au chargement et quand les données changent
  useEffect(() => {
    // Attendre un court instant avant de dessiner initialement pour éviter les erreurs de ResizeObserver
    const initialDrawTimeout = setTimeout(() => {
      drawChart();
    }, 50);
    
    // Observer le redimensionnement pour redessiner, avec debounce pour éviter les mises à jour trop fréquentes
    if (containerRef.current && !resizeObserver.current) {
      let animationFrameId: number | null = null;
      
      resizeObserver.current = new ResizeObserver(() => {
        // Annuler toute mise à jour en attente
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
        
        // Planifier la mise à jour pour le prochain frame d'animation
        animationFrameId = window.requestAnimationFrame(() => {
          drawChart();
          animationFrameId = null;
        });
      });
      
      resizeObserver.current.observe(containerRef.current);
    }
    
    // Nettoyage
    return () => {
      clearTimeout(initialDrawTimeout);
      
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = null;
      }
    };
  }, [skills, maxValue, colors, animationDuration, loaded, size, isMobile]);
  
  return (
    <ChartContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {title && <ChartTitle>{title}</ChartTitle>}
      
      <SVGContainer ref={containerRef}>
        <svg 
          ref={svgRef} 
          tabIndex={0}
          aria-describedby={`${id}-desc`}
        />
        <Tooltip ref={tooltipRef} />
      </SVGContainer>
      
      {showLegend && (
        <Legend>
          {[20, 40, 60, 80, 100].map(value => (
            <LegendItem key={value} color={colors.point}>
              {getSkillLevel(value)}
            </LegendItem>
          ))}
        </Legend>
      )}
    </ChartContainer>
  );
};

export default SkillRadarChart;